import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/services/supabaseClient';

// Aviso de novidade mostrado uma única vez por usuário. Quem já viu fica registrado
// na nuvem (zsb_notice_seen), então não reaparece em outro navegador nem computador.
// Para um novo aviso, troque NOTICE_ID.
const NOTICE_ID = 'ptax-fechamento-dia-anterior-2026-10';

const localKey = (email: string) => `zsb_notice_${NOTICE_ID}_${email}`;
const readLocal = (email: string) => { try { return localStorage.getItem(localKey(email)) === '1'; } catch { return false; } };
const writeLocal = (email: string) => { try { localStorage.setItem(localKey(email), '1'); } catch { /* sem armazenamento local */ } };

export default function UpdateNotice() {
  const { user, isImpersonating } = useAuth();
  const email = user?.email?.toLowerCase() ?? '';
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const firstName = (user?.responsibleName || user?.name || '').trim().split(/\s+/)[0];

  // Decide se mostra: só depois de consultar a nuvem. Em caso de dúvida (falha de rede),
  // NÃO mostra — melhor perder um aviso do que repeti-lo.
  useEffect(() => {
    setOpen(false);
    if (!email || isImpersonating || !supabase || readLocal(email)) return;
    let active = true;
    supabase.from('zsb_notice_seen').select('notice_id').eq('notice_id', NOTICE_ID).maybeSingle()
      .then(({ data, error }) => {
        if (!active || error) return;
        if (data) writeLocal(email); else setOpen(true);
      });
    return () => { active = false; };
  }, [email, isImpersonating]);

  const close = () => {
    setOpen(false);
    if (!email || !supabase) return;
    writeLocal(email);
    void supabase.from('zsb_notice_seen')
      .upsert({ owner: email, notice_id: NOTICE_ID }, { onConflict: 'owner,notice_id', ignoreDuplicates: true })
      .then(({ error }) => { if (error) console.warn('Não foi possível registrar o aviso como visto:', error.message); });
  };

  useEffect(() => {
    if (!open) return;
    buttonRef.current?.focus();
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="update-notice-title" className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center gap-3 bg-primary-700 px-6 py-5 text-white">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-2xl"><i className="ri-refresh-line" /></span>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-200">Novidade</p>
            <h2 id="update-notice-title" className="text-lg font-bold leading-tight">Atualização do PTAX</h2>
          </div>
        </div>
        <div className="space-y-3 px-6 py-5 text-sm leading-relaxed text-foreground-800">
          <p>Olá{firstName ? `, ${firstName}` : ''}!</p>
          <p>
            Agora, o PTAX é atualizado diariamente com base no <strong>encerramento do dia anterior</strong>.
            Isso traz mais segurança e praticidade para você e para as suas negociações.
          </p>
        </div>
        <div className="flex justify-end border-t border-background-200 bg-background-50 px-6 py-4">
          <button ref={buttonRef} type="button" onClick={close} className="rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-300">
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
}
